import React, { useState, useEffect } from 'react';
import { ZoomIn, ZoomOut, LayoutDashboard, RotateCcw, PanelRight, PanelRightClose, Maximize2, Minimize2, RefreshCw, TrendingUp, TrendingDown, HelpCircle } from 'lucide-react';
import { ToolType } from './types';

interface CryptoPrice {
  symbol: string;
  price: number;
  priceChange: number;
  lastUpdate: Date;
}

interface CanvasBottomBarProps {
  tool: ToolType;
  onToolChange: (tool: ToolType) => void;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  widgetCount: number;
  // Sidebar controls
  showSidebar: boolean;
  onToggleSidebar: () => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  // Templates
  showTemplatesPopup: boolean;
  onToggleTemplatesPopup: () => void;
  // Token sync
  onSyncTokens: () => void;
  // Onboarding
  onReplayOnboarding: () => void;
}

const CanvasBottomBar: React.FC<CanvasBottomBarProps> = ({
  tool,
  onToolChange,
  zoom,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  widgetCount,
  showSidebar,
  onToggleSidebar,
  isExpanded,
  onToggleExpanded,
  showTemplatesPopup,
  onToggleTemplatesPopup,
  onSyncTokens,
  onReplayOnboarding
}) => {
  // Crypto prices state
  const [prices, setPrices] = useState<{[key: string]: CryptoPrice}>({
    BTC: { symbol: 'BTC', price: 0, priceChange: 0, lastUpdate: new Date() },
    ETH: { symbol: 'ETH', price: 0, priceChange: 0, lastUpdate: new Date() },
    SOL: { symbol: 'SOL', price: 0, priceChange: 0, lastUpdate: new Date() },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch crypto prices
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        // Fetch current prices
        const tickerResponse = await fetch('https://api.binance.com/api/v3/ticker/price?symbols=["BTCUSDT","ETHUSDT","SOLUSDT"]');
        if (!tickerResponse.ok) throw new Error('Failed to fetch price data');
        const tickerData = await tickerResponse.json();
        
        // Fetch 24h price change
        const priceChangeResponse = await fetch('https://api.binance.com/api/v3/ticker/24hr?symbols=["BTCUSDT","ETHUSDT","SOLUSDT"]');
        if (!priceChangeResponse.ok) throw new Error('Failed to fetch price change data');
        const priceChangeData = await priceChangeResponse.json();
        
        const newPrices = { ...prices };
        
        // Process the data
        tickerData.forEach((ticker: any) => {
          const symbol = ticker.symbol.replace('USDT', '');
          if (newPrices[symbol]) {
            newPrices[symbol].price = parseFloat(ticker.price);
            newPrices[symbol].lastUpdate = new Date();
          }
        });
        
        priceChangeData.forEach((ticker: any) => {
          const symbol = ticker.symbol.replace('USDT', '');
          if (newPrices[symbol]) {
            newPrices[symbol].priceChange = parseFloat(ticker.priceChangePercent);
          }
        });
        
        setPrices(newPrices);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error fetching crypto prices:', err);
        setError('Failed to fetch price data');
        setLoading(false);
      }
    };

    // Fetch immediately on mount
    fetchPrices();
    
    // Set up interval to fetch every 20 seconds
    const interval = setInterval(fetchPrices, 20000);
    
    // Clean up interval
    return () => clearInterval(interval);
  }, []);

  // Format price with appropriate precision
  const formatPrice = (price: number) => {
    if (price === 0) return '$0.00';
    if (price < 1) return `$${price.toFixed(4)}`;
    if (price < 10) return `$${price.toFixed(2)}`;
    if (price < 1000) return `$${price.toFixed(2)}`;
    return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  // Format percentage change
  const formatPercentage = (change: number) => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}%`;
  };

  // Get style based on price change
  const getPriceChangeStyle = (change: number) => {
    return change >= 0 ? 'text-green-500' : 'text-red-500';
  };

  return (
    <div className="py-1 flex items-center justify-between px-3 bg-dark-grey/90 backdrop-blur-sm border-t border-gray-800/50">
      <div className="flex items-center gap-3">
        {/* Sidebar Controls - same style as zoom */}
        <div className="flex items-center glass rounded">
          <button
            onClick={onToggleSidebar}
            className="w-6 h-6 rounded-l flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors"
            title={showSidebar ? "Hide Sidebar" : "Show Sidebar"}
          >
            {showSidebar ? <PanelRightClose size={14} className="text-[#24c35b]" /> : <PanelRight size={14} className="text-[#24c35b]" />}
          </button>
          
          <button
            onClick={onToggleExpanded}
            className="w-6 h-6 rounded-r flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors"
            title={isExpanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
          >
            {isExpanded ? <Minimize2 size={14} className="text-[#24c35b]" /> : <Maximize2 size={14} className="text-[#24c35b]" />}
          </button>
        </div>
        
        <div className="w-px h-4 bg-gray-700/50"></div>
        
        {/* Zoom Controls */}
        <div className="flex items-center glass rounded">
          <button
            onClick={onZoomOut}
            className="w-6 h-6 rounded-l flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors"
            title="Zoom Out"
          >
            <ZoomOut size={14} className="text-[#24c35b]" />
          </button>
          
          <span className="text-xs font-medium text-black select-none min-w-[48px] text-center border-l border-r border-gray-700/50 py-1">
            {Math.round(zoom * 100)}%
          </span>
          
          <button
            onClick={onZoomIn}
            className="w-6 h-6 rounded-r flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors"
            title="Zoom In"
          >
            <ZoomIn size={14} className="text-[#24c35b]" />
          </button>
        </div>
        
        {/* Refresh Button */}
        <button
          onClick={onZoomReset}
          className="w-6 h-6 rounded flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors glass"
          title="Reset Zoom & Position"
        >
          <RotateCcw size={14} className="text-[#24c35b]" />
        </button>
      </div>
      
      {/* Center Section - Crypto Prices + Widget Count */}
      <div className="flex items-center gap-4">
        {/* Crypto Prices */}
        <div className="flex items-center gap-3">
          {loading ? (
            <div className="text-black text-xs">Loading prices...</div>
          ) : error ? (
            <div className="text-red-500 text-xs">{error}</div>
          ) : (
            <>
              {Object.values(prices).map((crypto, index) => (
                <div 
                  key={crypto.symbol} 
                  className="flex items-center gap-1.5"
                >
                  <span className={`font-medium text-xs ${crypto.symbol === 'BTC' ? 'text-[#f7931a]' : crypto.symbol === 'ETH' ? 'text-[#627eea]' : 'text-[#00ffbd]'}`}>
                    {crypto.symbol}
                  </span>
                  <span className="text-xs text-black">{formatPrice(crypto.price)}</span>
                  <span className={`flex items-center text-xs ${getPriceChangeStyle(crypto.priceChange)}`}>
                    {crypto.priceChange >= 0 ? (
                      <TrendingUp size={10} className="mr-0.5" />
                    ) : (
                      <TrendingDown size={10} className="mr-0.5" />
                    )}
                    {formatPercentage(crypto.priceChange)}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
        
        <div className="w-px h-4 bg-gray-700/50"></div>
        
        {/* Widget Count */}
        <span className="text-xs text-black leading-none">
          {widgetCount} widget{widgetCount !== 1 ? 's' : ''}
        </span>
      </div>
      
      <div className="flex items-center gap-3">
        {/* Token Sync Button */}
        <button
          onClick={onSyncTokens}
          className="w-6 h-6 rounded flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors glass"
          title="Sync tokens across widgets"
        >
          <RefreshCw size={14} className="text-[#24c35b]" />
        </button>
        
        <div className="w-px h-4 bg-gray-700/50"></div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={onToggleTemplatesPopup}
            className={`w-6 h-6 rounded flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors ${showTemplatesPopup ? 'bg-white/10' : ''}`}
            title="Templates"
            data-onboarding="templates-button"
          >
            <LayoutDashboard size={14} className="text-[#24c35b]" />
          </button>
          
          {/* Onboarding Button */}
          <button
            onClick={onReplayOnboarding}
            className="w-6 h-6 rounded flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors glass"
            title="Replay Guide"
          >
            <HelpCircle size={14} className="text-[#24c35b]" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CanvasBottomBar;